/**
 *
 * Pagination
 *
 */

import PropTypes from 'prop-types';
import React, { memo, useMemo } from 'react';

import MoreButton from './components/MoreButton';
import ArrowLeftIcon from '../../../assets/v2/ArrowLeftIcon';
import ArrowRightIcon from '../../../assets/v2/ArrowRightIcon';
import Dropdown from '../Dropdown';
import {
  DEFAULT_MAX_PAGE_BUTTONS,
  DEFAULT_RESULTS_PER_PAGE_OPTIONS,
} from './constants';
import {
  ArrowButton,
  ControlsContainer,
  PageButton,
  PaginationContainer,
  ResultsInfoContainer,
  ResultsPerPageContainer,
} from './Pagination.styles';
import { getPageButtons } from './utils';
import Skeleton from '../Skeleton';

const Pagination = ({
  disabled,
  onPageChange,
  onResultsPerPageChange,
  page = 1,
  maxPageButtons = DEFAULT_MAX_PAGE_BUTTONS,
  maxResults,
  resultsPerPage = 20,
  resultsPerPageOptions = DEFAULT_RESULTS_PER_PAGE_OPTIONS,
  total,
}) => {
  const isDisabled = disabled || total === 0;
  // When the backend only serves the first `maxResults` matches, pages past that point
  // cannot be loaded, so the page selector stops there while the count stays honest.
  const isResultsCapped = !!maxResults && total > maxResults;
  const pageableTotal = isResultsCapped ? maxResults : total;
  const lastPage = Math.ceil(pageableTotal / resultsPerPage) || 1;
  const resultsLowerBound = (page - 1) * resultsPerPage + 1;
  const resultsUpperBound = Math.min(page * resultsPerPage, pageableTotal);

  const pageButtons = useMemo(
    () => getPageButtons(page, lastPage, maxPageButtons),
    [page, lastPage, maxPageButtons],
  );

  const MoreBtn = useMemo(
    () => (
      <MoreButton
        disabled={isDisabled}
        lastPage={lastPage}
        page={page}
        onPageChange={onPageChange}
      />
    ),
    [isDisabled, lastPage, page, onPageChange],
  );

  return (
    <PaginationContainer>
      <ControlsContainer>
        {pageButtons.length > 1 && (
          <>
            <ArrowButton
              disabled={isDisabled || page === 1}
              size="iconSmall"
              onClick={() => onPageChange(page - 1)}
            >
              <ArrowLeftIcon />
            </ArrowButton>

            {pageButtons[0] > 1 && MoreBtn}

            {pageButtons.map((value) => (
              <PageButton
                color={value === page ? 'secondary' : undefined}
                disabled={isDisabled}
                key={value}
                size="iconSmall"
                variant={value === page ? 'contained' : undefined}
                onClick={() => onPageChange(value)}
              >
                {value.toLocaleString()}
              </PageButton>
            ))}

            {pageButtons[pageButtons.length - 1] < lastPage && MoreBtn}

            <ArrowButton
              disabled={isDisabled || page === lastPage}
              size="iconSmall"
              onClick={() => onPageChange(page + 1)}
            >
              <ArrowRightIcon />
            </ArrowButton>
          </>
        )}
      </ControlsContainer>
      <ResultsPerPageContainer>
        <Dropdown
          disabled={isDisabled}
          value={resultsPerPage}
          options={resultsPerPageOptions}
          onChange={onResultsPerPageChange}
        />
      </ResultsPerPageContainer>

      <ResultsInfoContainer>
        {disabled && !total && <Skeleton width={220} height={12} />}
        {total > 0 && (
          <span>
            {`Showing ${resultsLowerBound.toLocaleString()} - ${resultsUpperBound.toLocaleString()} of ${total.toLocaleString()} results`}
          </span>
        )}
      </ResultsInfoContainer>
    </PaginationContainer>
  );
};

Pagination.propTypes = {
  disabled: PropTypes.bool,
  maxPageButtons: PropTypes.number,
  /** Highest number of results the backend will page through; pages beyond it are hidden. */
  maxResults: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onResultsPerPageChange: PropTypes.func,
  page: PropTypes.number.isRequired,
  resultsPerPage: PropTypes.number,
  resultsPerPageOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    }),
  ),
  total: PropTypes.number,
};

export default memo(Pagination);
